import { User, JobPosting, Application, ApplicationSubmission, ApplicationStatus, Interview } from '../types';

const TOKEN_KEY = 'sanscraft_careers_token';
const USER_KEY = 'sanscraft_careers_user';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): User | null {
  const data = localStorage.getItem(USER_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

export function setAuthData(token: string, user: User) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuthData() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(endpoint, {
    ...options,
    headers,
  });

  const data: any = await res.json();
  if (!res.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data as T;
}

export const api = {
  // Auth
  async register(data: any): Promise<{ user: User; token: string; message?: string }> {
    const res = await request<{ user: User; token: string; message?: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (res.token && res.user) {
      setAuthData(res.token, res.user);
    }
    return res;
  },

  async login(data: any): Promise<{ user: User; token: string }> {
    const res = await request<{ user: User; token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    setAuthData(res.token, res.user);
    return res;
  },

  async getMe(): Promise<{ user: User }> {
    return request<{ user: User }>('/api/auth/me');
  },

  // Admin User Management
  async getUsers(): Promise<{ users: User[] }> {
    return request<{ users: User[] }>('/api/admin/users');
  },

  async approveUser(userId: string, role: string = 'hiring_team', approved: boolean = true): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/admin/users/${userId}/approve`, {
      method: 'PATCH',
      body: JSON.stringify({ role, hr_approved: approved }),
    });
  },

  // Jobs
  async getJobs(params?: { q?: string; department?: string; type?: string }): Promise<{ jobs: JobPosting[] }> {
    const query = new URLSearchParams();
    if (params?.q) query.append('q', params.q);
    if (params?.department) query.append('department', params.department);
    if (params?.type) query.append('type', params.type);
    
    const queryString = query.toString() ? `?${query.toString()}` : '';
    return request<{ jobs: JobPosting[] }>(`/api/jobs${queryString}`);
  },

  async createJob(jobData: Partial<JobPosting>): Promise<{ job: JobPosting; message: string }> {
    return request<{ job: JobPosting; message: string }>('/api/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  },

  async updateJob(id: string, jobData: Partial<JobPosting>): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/jobs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(jobData),
    });
  },

  async deleteJob(id: string): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/jobs/${id}`, {
      method: 'DELETE',
    });
  },

  // Applications
  async submitApplication(submission: ApplicationSubmission): Promise<{ application: Application; message: string }> {
    return request<{ application: Application; message: string }>('/api/applications', {
      method: 'POST',
      body: JSON.stringify(submission),
    });
  },

  async getMyApplications(): Promise<{ applications: Application[] }> {
    return request<{ applications: Application[] }>('/api/applications/my');
  },

  async getAllApplications(): Promise<{ applications: Application[] }> {
    return request<{ applications: Application[] }>('/api/applications/all');
  },

  async updateApplicationStatus(id: string, status: ApplicationStatus, reviewer_notes?: string): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/applications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reviewer_notes }),
    });
  },

  // Interview Scheduling
  async scheduleInterview(data: {
    application_id: string;
    job_title: string;
    candidate_name: string;
    candidate_email: string;
    scheduled_at: string;
    notes?: string;
  }): Promise<{ interview: Interview; message: string }> {
    return request<{ interview: Interview; message: string }>('/api/interviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getMyInterviews(): Promise<{ interviews: Interview[] }> {
    return request<{ interviews: Interview[] }>('/api/interviews/my');
  },

  async getAllInterviews(): Promise<{ interviews: Interview[] }> {
    return request<{ interviews: Interview[] }>('/api/interviews/all');
  },

  // WebRTC Signaling
  async sendSignal(roomId: string, sender: string, payload: any): Promise<{ status: string }> {
    return request<{ status: string }>(`/api/interviews/${roomId}/signal`, {
      method: 'POST',
      body: JSON.stringify({ action: 'send', sender, payload }),
    });
  },

  async pollSignals(roomId: string, sender: string): Promise<{ signals: any[] }> {
    return request<{ signals: any[] }>(`/api/interviews/${roomId}/signal`, {
      method: 'POST',
      body: JSON.stringify({ action: 'poll', sender }),
    });
  },
};
